/**
 * Katalógus-riasztások — the Catalogue alerts view of handover rev7 §11.7 and
 * the watcher of §11.9.
 *
 * "After each sync, every open order's line snapshots are compared to the
 * publication. An allergen, gluten-free or verification change blocks labels
 * and alerts the signatory and catering (C12, K9). A withdrawn dish alerts the
 * same way."
 *
 * §11.7 gives the signatory one pair of actions on each order: **keep the
 * snapshot**, or **update it** — which writes a new version and sends the
 * customer a corrected allergen list (U14). The screen calculates nothing: the
 * fields that moved, what they moved from and to, and whether an update is
 * possible at all all come back from the server.
 *
 * A withdrawn dish cannot be resolved by updating the snapshot — there is no
 * published dish to snapshot — so the button is not offered and the screen
 * says where that order goes instead (§11.8, the staff editor).
 */

import { useState } from 'react'
import { Link } from 'react-router-dom'

import { asDate, asMoment, fmt, Loading, statusText, useConsoleAction, useConsoleData } from '../common.tsx'

interface Change {
  id: number
  dish_id: string
  dish: string
  field: string
  field_label: string | null
  was: string | null
  now: string | null
  version: number
  detected_at: string
  resolution: string | null
  resolution_note: string | null
  resolved_by: string | null
  resolved_at: string | null
  resolved_version: number | null
}

interface AlertOrder {
  id: string
  ref: string
  status: string
  status_reason: string | null
  event_date: string
  guests: number
  gf_guests: number
  customer_name: string
  labels_blocked: boolean
  changes: Change[] | null
  open_changes: number
  has_withdrawn: boolean
}

export function Alerts(): JSX.Element {
  const [includeResolved, setIncludeResolved] = useState(false)
  const { data, error, loading, reload } = useConsoleData<{ orders: AlertOrder[] }>(
    'catalogue_alerts',
    { include_resolved: includeResolved },
  )
  const { run, busy, error: actionError } = useConsoleAction()
  const [note, setNote] = useState<Record<string, string>>({})

  async function resolve(order: AlertOrder, decision: 'keep' | 'update'): Promise<void> {
    if (
      await run('catalogue_resolve', {
        order_id: order.id,
        decision,
        note: note[order.id] ?? '',
      })
    ) {
      setNote((previous) => ({ ...previous, [order.id]: '' }))
      reload()
    }
  }

  return (
    <section data-testid="view-alerts">
      <h2>Katalógus-riasztások</h2>
      <p className="lead">
        §11.9: minden szinkron után összevetjük a nyitott rendelések fogás-pillanatképeit a
        kiadással. Allergén, gluténmentesség vagy ellenőrzési állapot változása — és a kivezetett
        fogás — letiltja a címkézést, amíg valaki nem dönt.
      </p>

      <label>
        <input
          type="checkbox"
          checked={includeResolved}
          onChange={(event) => setIncludeResolved(event.target.checked)}
          data-testid="alerts-include-resolved"
        />
        {' '}A lezárt riasztások is
      </label>

      <Loading loading={loading} error={error} />
      {actionError !== null && <p className="note error" data-testid="action-error">{actionError}</p>}

      {data !== null && data.orders.length === 0 && (
        <p className="note" data-testid="alerts-empty">Nincs feloldásra váró katalógus-riasztás.</p>
      )}

      {data?.orders.map((order) => (
        <article className="card" key={order.id} data-testid={`alert-${order.ref}`}>
          <h3>
            <Link to={`/staff/orders/${order.id}`}>{order.ref}</Link>
            {' · '}
            {asDate(order.event_date, true)}
            {' · '}
            {fmt.n(order.guests)} fő
            {' · '}
            {statusText(order.status, order.status_reason)}
          </h3>

          {order.labels_blocked && (
            <p className="note error" data-testid={`blocked-${order.ref}`}>
              CÍMKÉZÉS BLOKKOLVA — a K6 címkék nem nyomtathatók, amíg a riasztás nyitva van (§11.6).
            </p>
          )}

          <table>
            <thead>
              <tr>
                <th>Fogás</th>
                <th>Mező</th>
                <th>Rendelésben</th>
                <th>Katalógusban</th>
                <th>Észlelve</th>
                <th>Döntés</th>
              </tr>
            </thead>
            <tbody>
              {(order.changes ?? []).map((change) => (
                <tr key={change.id}>
                  <td>{change.dish}</td>
                  <td>{change.field_label ?? change.field}</td>
                  <td>{change.was ?? '—'}</td>
                  <td>{change.now ?? '—'}</td>
                  <td>{asMoment(change.detected_at)}</td>
                  <td>
                    {change.resolution === null
                      ? <span className="tag warn">nyitott</span>
                      : (
                        <>
                          {change.resolution === 'keep' ? 'pillanatkép megtartva' : 'pillanatkép frissítve'}
                          {change.resolved_version !== null && ` · v${change.resolved_version}`}
                          {change.resolved_by !== null && ` · ${change.resolved_by}`}
                          {change.resolved_at !== null && ` · ${asMoment(change.resolved_at)}`}
                        </>
                      )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {order.open_changes > 0 && (
            <>
              {order.has_withdrawn && (
                <p className="note gap" data-testid={`withdrawn-${order.ref}`}>
                  Kivezetett fogás van a rendelésben: a pillanatkép nem frissíthető, mert a
                  kiadásban nincs mire. Ezt a rendelésszerkesztőben, a Planteen által
                  kezdeményezett módosításként kell rendezni (§11.8).
                </p>
              )}

              <label htmlFor={`note-${order.id}`}>Megjegyzés (a rendelés eseménynaplójába kerül)</label>
              <textarea
                id={`note-${order.id}`}
                rows={2}
                value={note[order.id] ?? ''}
                onChange={(event) =>
                  setNote((previous) => ({ ...previous, [order.id]: event.target.value }))}
                data-testid={`alert-note-${order.ref}`}
              />

              <div className="actions">
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => void resolve(order, 'keep')}
                  data-testid={`keep-${order.ref}`}
                >
                  Pillanatkép megtartása
                </button>
                <button
                  type="button"
                  className="primary"
                  disabled={busy || order.has_withdrawn}
                  onClick={() => void resolve(order, 'update')}
                  data-testid={`update-${order.ref}`}
                >
                  Pillanatkép frissítése (új verzió, U14)
                </button>
              </div>
            </>
          )}
        </article>
      ))}
    </section>
  )
}
